import { GLASS_FORCE_ATTR } from "./native-glass-collect";
import { requestNativeGlassRefresh } from "./native-glass-settings";

/** Gestures that move content on every tick: a canvas pan/zoom (React Flow
 *  move start/end), a node drag, and a frame window drag or resize. */
type NativeGlassGesture = "viewport" | "node-drag" | "window-drag";

const VIEWPORT_SELECTOR = ".react-flow__viewport";
/** With no begin or viewport tick for this long, a gesture is assumed to
 *  have ended without its end callback. */
export const NATIVE_GLASS_GESTURE_QUIET_MS = 1000;
/** Window events that end every pointer gesture, so a missed move end or
 *  drag stop cannot leave the gesture flag stuck. Pointer events are caught
 *  in the capture phase; `blur` is not, because element blurs reach window
 *  listeners only through capture and must not end a gesture. */
const GESTURE_END_EVENTS = [
  ["pointerup", true],
  ["pointercancel", true],
  ["blur", false],
] as const;

const activeGestures = new Set<NativeGlassGesture>();
let quietTimer: ReturnType<typeof setTimeout> | null = null;

export const isNativeGlassGestureActive = () => activeGestures.size > 0;

const stopSafetyNet = () => {
  if (quietTimer !== null) clearTimeout(quietTimer);
  quietTimer = null;
  if (typeof window === "undefined") return;
  for (const [type, capture] of GESTURE_END_EVENTS) {
    window.removeEventListener(type, settleNativeGlassGestures, capture);
  }
};

const armQuietTimer = () => {
  if (quietTimer !== null) clearTimeout(quietTimer);
  quietTimer = setTimeout(
    settleNativeGlassGestures,
    NATIVE_GLASS_GESTURE_QUIET_MS,
  );
};

/** Ends every active gesture with one single-pass collection, so anything
 *  the gestures skipped (viewport-internal mutations, the transition window)
 *  is corrected against the settled DOM. */
function settleNativeGlassGestures() {
  if (activeGestures.size === 0) return;
  activeGestures.clear();
  stopSafetyNet();
  requestNativeGlassRefresh({ trackTransition: false });
}

/** Starts `gesture`, or keeps it alive when it is already running. */
export const beginNativeGlassGesture = (gesture: NativeGlassGesture) => {
  if (activeGestures.size === 0 && typeof window !== "undefined") {
    for (const [type, capture] of GESTURE_END_EVENTS) {
      window.addEventListener(type, settleNativeGlassGestures, capture);
    }
  }
  activeGestures.add(gesture);
  armQuietTimer();
};

/** The last gesture to end settles native glass. */
export const endNativeGlassGesture = (gesture: NativeGlassGesture) => {
  if (!activeGestures.has(gesture)) return;
  if (activeGestures.size > 1) {
    activeGestures.delete(gesture);
    return;
  }
  settleNativeGlassGestures();
};

/** Drops gestures that can no longer end, e.g. when the canvas unmounts. */
export const clearNativeGlassGestures = () => {
  activeGestures.clear();
  stopSafetyNet();
};

/** Viewport ticks already request a refresh and their frame plates come from
 *  the model, so the viewport transform and node moves need no extra pass,
 *  and must not keep the transition window open while the gesture runs.
 *  Each tick keeps a running gesture alive past the quiet timeout. */
export const requestViewportChangeNativeGlassRefresh = () => {
  if (isNativeGlassGestureActive()) armQuietTimer();
  requestNativeGlassRefresh({ trackTransition: false });
};

const insideCanvasViewport = (target: Node) => {
  const element = target instanceof Element ? target : target.parentElement;
  return element?.closest(VIEWPORT_SELECTOR) != null;
};

const isCssForcedStyleChange = (record: MutationRecord) =>
  record.type === "attributes" &&
  (record.attributeName === "style" || record.attributeName === "class") &&
  record.target instanceof Element &&
  record.target.closest(`[${GLASS_FORCE_ATTR}="css"]`) != null;

/** Whether a body mutation batch needs a collection pass. Style changes on
 *  CSS-forced surfaces never do; while a gesture runs, neither do mutations
 *  confined to the canvas viewport, which the viewport tick refresh and the
 *  settle pass already cover. */
export const nativeGlassMutationsNeedPass = (records: MutationRecord[]) =>
  !records.every(isCssForcedStyleChange) &&
  !(
    isNativeGlassGestureActive() &&
    records.every((record) => insideCanvasViewport(record.target))
  );
